import { apiSlice } from "../../../features/api/apiSlice";

/**
 * Unwrap a Laravel API Resource response.
 * Single resource  → {"data": {...}}          → returns the inner object
 * Resource collection → {"data": [...]}       → returns the array
 * Plain JSON         → {...} (stats, etc.)    → returns as-is
 */
function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res;
}

function arr(res: any): any[] {
  const d = pick(res);
  return Array.isArray(d) ? d : [];
}

/**
 * Laravel paginator (via TicketResource::collection($paginator)) returns:
 *   { data: [...], links: {...}, meta: { total, per_page, current_page, ... } }
 */
function paginationMeta(r: any) {
  return {
    total:        r?.meta?.total        ?? 0,
    per_page:     r?.meta?.per_page     ?? 20,
    current_page: r?.meta?.current_page ?? 1,
  };
}

/** Send FormData when files are present; otherwise send plain JSON. */
function buildBody<T extends Record<string, unknown>>(
  fields: T,
  files?: File[],
  fileKey = "attachments",
): T | FormData {
  if (!files?.length) return fields;
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v == null) return;
    // PHP's (bool)"false" === true, so booleans must be sent as "1"/"0"
    if (typeof v === "boolean") fd.append(k, v ? "1" : "0");
    else fd.append(k, String(v));
  });
  files.forEach((f) => fd.append(`${fileKey}[]`, f));
  return fd;
}

// ── Types ────────────────────────────────────────────────────────────────────

export type TicketStatus   = "open" | "in_progress" | "resolved" | "closed";
export type TicketPriority = "low" | "medium" | "high" | "urgent";

export type TicketCategory = {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
};

export type TicketAttachment = {
  id: number;
  ticket_id: number;
  reply_id?: number | null;
  uploaded_by: number;
  filename: string;
  mime_type: string;
  size: number;
  url: string;
  created_at: string;
};

export type TicketReply = {
  id: number;
  ticket_id: number;
  user_id: number;
  body: string;
  is_internal: boolean;
  created_at: string;
  user?: { id: number; name: string; whois: string };
  attachments?: TicketAttachment[];
};

export type TicketHistory = {
  id: number;
  ticket_id: number;
  action: string;
  from_value?: string;
  to_value?: string;
  performed_by?: number;
  note?: string;
  created_at: string;
  actor?: { id: number; name: string };
};

export type Ticket = {
  id: number;
  reference: string;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category_id?: number;
  user_id: number;
  assigned_to?: number;
  resolved_at?: string;
  closed_at?: string;
  created_at: string;
  updated_at: string;
  category?: TicketCategory;
  user?: { id: number; name: string; whois: string; email: string };
  assignee?: { id: number; name: string };
  replies?: TicketReply[];
  history?: TicketHistory[];
  attachments?: TicketAttachment[];
  replies_count?: number;
};

export type TicketStats = {
  open: number;
  in_progress: number;
  resolved: number;
  closed: number;
  urgent: number;
  total: number;
};

export type CreateTicketPayload = {
  subject: string;
  description: string;
  priority: TicketPriority;
  category_id?: number;
  attachments?: File[];
};

export type ReplyPayload = {
  body: string;
  is_internal?: boolean;
  attachments?: File[];
};

export type UpdateStatusPayload = {
  status: TicketStatus;
  note?: string;
};

export type AssignTicketPayload = {
  assigned_to: number;
  note?: string;
};

export type AdminTicketListResult = {
  data: Ticket[];
  total: number;
  per_page: number;
  current_page: number;
};

// ── Endpoints ─────────────────────────────────────────────────────────────────

export const supportApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({

    // ── Public: active categories for ticket form ────────────────────────────
    getTicketCategories: b.query<TicketCategory[], void>({
      query: () => "/support/categories",
      transformResponse: (r) => arr(r),
      providesTags: [{ type: "TicketCategories" as const, id: "LIST" }],
    }),

    // ── Member: my tickets ───────────────────────────────────────────────────
    getMyTickets: b.query<Ticket[], void>({
      query: () => "/support/tickets",
      transformResponse: (r) => arr(r),
      providesTags: [{ type: "MyTickets" as const, id: "LIST" }],
    }),

    getMyTicket: b.query<Ticket, string | number>({
      query: (id) => `/support/tickets/${id}`,
      transformResponse: (r) => pick(r),
      providesTags: (_, __, id) => [{ type: "MyTickets" as const, id }],
    }),

    // transformResponse unwraps {"data":{...}} so .unwrap() returns the ticket
    createTicket: b.mutation<Ticket, CreateTicketPayload>({
      query: ({ attachments, ...fields }) => ({
        url: "/support/tickets",
        method: "POST",
        body: buildBody(fields as Record<string, unknown>, attachments),
      }),
      transformResponse: (r) => pick(r),
      invalidatesTags: [{ type: "MyTickets" as const, id: "LIST" }],
    }),

    closeMyTicket: b.mutation<Ticket, string | number>({
      query: (id) => ({
        url: `/support/tickets/${id}/status`,
        method: "POST",
        body: { status: "closed" },
      }),
      transformResponse: (r) => pick(r),
      invalidatesTags: (_, __, id) => [
        { type: "MyTickets" as const, id },
        { type: "MyTickets" as const, id: "LIST" },
      ],
    }),

    resolveMyTicket: b.mutation<Ticket, string | number>({
      query: (id) => ({
        url: `/support/tickets/${id}/status`,
        method: "POST",
        body: { status: "resolved" },
      }),
      transformResponse: (r) => pick(r),
      invalidatesTags: (_, __, id) => [
        { type: "MyTickets" as const, id },
        { type: "MyTickets" as const, id: "LIST" },
      ],
    }),

    addReply: b.mutation<TicketReply, { id: string | number; body: ReplyPayload }>({
      query: ({ id, body: { attachments, ...rest } }) => ({
        url: `/support/tickets/${id}/replies`,
        method: "POST",
        body: buildBody(rest as Record<string, unknown>, attachments),
      }),
      transformResponse: (r) => pick(r),
      // Invalidate the individual ticket so the full thread refreshes
      invalidatesTags: (_, __, { id }) => [
        { type: "MyTickets" as const, id },
        { type: "MyTickets" as const, id: "LIST" },
      ],
    }),

    // ── Admin: all tickets (paginated) ───────────────────────────────────────
    getAdminTickets: b.query<
      AdminTicketListResult,
      { status?: string; priority?: string; category_id?: number; page?: number; search?: string }
    >({
      query: (params) => ({ url: "/admin/support/tickets", params }),
      // Laravel paginator wraps data in { data:[...], meta:{ total, per_page, current_page } }
      transformResponse: (r: any) => ({
        data: arr(r),
        ...paginationMeta(r),
      }),
      providesTags: [{ type: "AdminTickets" as const, id: "LIST" }],
    }),

    getAdminTicket: b.query<Ticket, string | number>({
      query: (id) => `/admin/support/tickets/${id}`,
      transformResponse: (r) => pick(r),
      providesTags: (_, __, id) => [{ type: "AdminTickets" as const, id }],
    }),

    adminResolveTicket: b.mutation<Ticket, string | number>({
      query: (id) => ({ url: `/admin/support/tickets/${id}/resolve`, method: "POST" }),
      transformResponse: (r) => pick(r),
      invalidatesTags: (_, __, id) => [
        { type: "AdminTickets" as const, id },
        { type: "AdminTickets" as const, id: "LIST" },
        { type: "AdminTickets" as const, id: "STATS" },
        { type: "MyTickets" as const, id },
      ],
    }),

    adminAddReply: b.mutation<TicketReply, { id: string | number; body: ReplyPayload }>({
      query: ({ id, body: { attachments, ...rest } }) => ({
        url: `/admin/support/tickets/${id}/replies`,
        method: "POST",
        body: buildBody(rest as Record<string, unknown>, attachments),
      }),
      transformResponse: (r) => pick(r),
      invalidatesTags: (_, __, { id }) => [
        { type: "AdminTickets" as const, id },
        { type: "AdminTickets" as const, id: "LIST" },
      ],
    }),

    updateTicketStatus: b.mutation<Ticket, { id: string | number; body: UpdateStatusPayload }>({
      query: ({ id, body }) => ({
        url: `/admin/support/tickets/${id}/status`,
        method: "POST",
        body,
      }),
      transformResponse: (r) => pick(r),
      invalidatesTags: (_, __, { id }) => [
        { type: "AdminTickets" as const, id },
        { type: "AdminTickets" as const, id: "LIST" },
        // Also invalidate the member-facing cached version
        { type: "MyTickets" as const, id },
      ],
    }),

    assignTicket: b.mutation<Ticket, { id: string | number; body: AssignTicketPayload }>({
      query: ({ id, body }) => ({
        url: `/admin/support/tickets/${id}/assign`,
        method: "POST",
        body,
      }),
      transformResponse: (r) => pick(r),
      invalidatesTags: (_, __, { id }) => [
        { type: "AdminTickets" as const, id },
        { type: "AdminTickets" as const, id: "LIST" },
      ],
    }),

    // ── Admin: categories CRUD ───────────────────────────────────────────────
    getAdminCategories: b.query<TicketCategory[], void>({
      query: () => "/admin/support/categories",
      transformResponse: (r) => arr(r),
      providesTags: [{ type: "TicketCategories" as const, id: "LIST" }],
    }),

    adminCreateCategory: b.mutation<TicketCategory, { name: string; description?: string }>({
      query: (body) => ({ url: "/admin/support/categories", method: "POST", body }),
      transformResponse: (r) => pick(r),
      invalidatesTags: [{ type: "TicketCategories" as const, id: "LIST" }],
    }),

    adminUpdateCategory: b.mutation<TicketCategory, { id: number; name: string; description?: string }>({
      query: ({ id, ...body }) => ({ url: `/admin/support/categories/${id}`, method: "PUT", body }),
      transformResponse: (r) => pick(r),
      invalidatesTags: [{ type: "TicketCategories" as const, id: "LIST" }],
    }),

    adminToggleCategory: b.mutation<TicketCategory, number>({
      query: (id) => ({ url: `/admin/support/categories/${id}/toggle`, method: "PATCH" }),
      transformResponse: (r) => pick(r),
      invalidatesTags: [{ type: "TicketCategories" as const, id: "LIST" }],
    }),

    // ── Admin: stats ─────────────────────────────────────────────────────────
    // Backend returns plain JSON (no data wrapper), so pick() falls through to r
    getTicketStats: b.query<TicketStats, void>({
      query: () => "/admin/support/stats",
      transformResponse: (r: any) => r as TicketStats,
      providesTags: [{ type: "AdminTickets" as const, id: "STATS" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTicketCategoriesQuery,
  useGetMyTicketsQuery,
  useGetMyTicketQuery,
  useCreateTicketMutation,
  useCloseMyTicketMutation,
  useResolveMyTicketMutation,
  useAddReplyMutation,
  useGetAdminTicketsQuery,
  useGetAdminTicketQuery,
  useAdminResolveTicketMutation,
  useAdminAddReplyMutation,
  useUpdateTicketStatusMutation,
  useAssignTicketMutation,
  useGetAdminCategoriesQuery,
  useAdminCreateCategoryMutation,
  useAdminUpdateCategoryMutation,
  useAdminToggleCategoryMutation,
  useGetTicketStatsQuery,
} = supportApi;
